import * as DocumentPicker from 'expo-document-picker';
import type * as ImagePicker from 'expo-image-picker';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useValidatePaymentMethod } from '@/api/customers/use-validate-payment-method';
import ContactSupportModal from '@/components/contact-support-modal';
import VerificationScreen from '@/components/verification-screen';
import { uniqByFilename } from '@/core';
import { Button, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

import { NavigationButton, NavigationButtonTypes } from '../navigation-button';
import { PaymentInstructions } from './payment-instructions';
import { PaymentVerificationPending } from './payment-verification-pending';

export default function VerifyPaymentMethod() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const { trip_id } = useLocalSearchParams();
  const [status, setStatus] = React.useState<
    'uploaded' | 'successful' | 'rejected' | ''
  >('');
  const [images, setImages] = React.useState<ImagePicker.ImagePickerAsset[]>();
  const [contactSupportModalVisible, setContactSupportModalVisible] =
    React.useState(false);

  const { data: customer, isFetching: isFetchingCustomer } = useCustomer();

  const {
    mutateAsync: validatePaymentMethod,
    isPending: isValidatingPaymentMethod,
  } = useValidatePaymentMethod();

  useFocusEffect(
    React.useCallback(() => {
      setStatus('');
    }, [])
  );

  React.useEffect(() => {
    if (!images || images.length === 0) return;

    const handleValidatePaymentMethod = async () => {
      try {
        await validatePaymentMethod({ images: images });

        setStatus('uploaded');
      } catch (error) {
        showErrorMessage(error.message);
        setStatus('rejected');
      }
    };

    handleValidatePaymentMethod();
  }, [images]);

  const handleUploadDocuments = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/jpeg', 'image/png', 'image/heic'],
        copyToCacheDirectory: false,
        multiple: true,
      });

      if (!result.canceled) {
        const assets = await Promise.all(
          result.assets.map(async (asset) => {
            const response = await fetch(asset.uri);
            const blob = await response.blob();

            const file = new File([blob], asset.name || 'file.jpg', {
              type: asset.mimeType || 'image/jpeg',
            });

            return file;
          })
        );

        setImages(uniqByFilename([...(images || []), ...assets]));
      }
    } catch (error) {
      console.error('Error picking file:', error);
    }
  };

  const goBack = () => {
    if (trip_id) {
      router.navigate(`/trips/${trip_id}/details`);
    } else {
      router.navigate('/trips');
    }
  };

  const isDesktop = ['md', 'lg'].includes(UnistylesRuntime.breakpoint);

  function renderBackButton() {
    return (
      <NavigationButton
        type={NavigationButtonTypes.back}
        onPress={goBack}
        style={styles.backButton}
      />
    );
  }

  const screenOptions = isDesktop
    ? { headerShown: false }
    : {
        title: 'Verify Your Payment Method',
        headerTitle: 'Verify Your Payment Method',
        headerLeft: renderBackButton,
        headerShown: true,
        headerStyle: {
          backgroundColor: theme.colors.background_1,
        },
        headerTintColor: theme.colors.text.primary,
        headerShadowVisible: false,
        headerTitleStyle: {
          fontSize: 17,
        },
      };

  if (customer && status === 'uploaded') {
    return <PaymentVerificationPending onDone={goBack} />;
  }

  if (customer && status !== '') {
    return (
      <VerificationScreen
        status={status}
        name="payment method"
        onGoBack={goBack}
      />
    );
  }

  if (isFetchingCustomer || isValidatingPaymentMethod) {
    return <Loader />;
  }

  return (
    <View style={styles.page}>
      <View style={styles.container}>
        <Stack.Screen options={screenOptions} />

        {isDesktop ? (
          <View style={{ gap: 16, paddingHorizontal: 16 }}>
            <View style={{ alignSelf: 'flex-start' }}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  router.back();
                }}
              />
            </View>

            <Text type="largeTitle" style={{ textAlign: 'center' }}>
              Verify Your Payment Method
            </Text>
          </View>
        ) : null}

        <View style={styles.content}>
          <PaymentInstructions />

          <View style={{ alignItems: 'center', gap: 16 }}>
            <View style={{ flexDirection: 'row' }}>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Need help?{` `}
              </Text>

              <Pressable
                onPress={() => {
                  setContactSupportModalVisible(true);
                }}
              >
                <Text
                  type="caption1"
                  style={{
                    color: theme.colors.text.secondary,
                    textDecorationLine: 'underline',
                  }}
                >
                  Contact support.
                </Text>
              </Pressable>
            </View>

            <Button
              text="Upload Screenshot"
              type="primary"
              onPress={handleUploadDocuments}
            />
          </View>
        </View>
      </View>

      <ContactSupportModal
        isVisible={contactSupportModalVisible}
        onDismiss={() => setContactSupportModalVisible(false)}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  page: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    gap: {
      md: 16,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
  backButton: {
    marginLeft: 16,
  },
  content: {
    flex: 1,
    justifyContent: 'space-between',
    padding: 16,
    paddingBottom: 32,
    gap: {
      xs: 16,
      md: 32,
    },
  },
}));
